import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Loader2, RefreshCw, Lock } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import type { GoogleAdsIntegration } from '@/hooks/useGoogleAdsConnection';
import { useGoogleAdsConversions } from '@/hooks/useGoogleAdsConversions';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface Props {
  integration: GoogleAdsIntegration;
}

/**
 * Conversions section of the Google Ads card: setup state (auto-tagging,
 * enhanced conversions for leads, the two upload actions), upload counts for
 * the last 7 days, the last error, and "Repair setup".
 */
export function GoogleAdsConversionsCard({ integration }: Props) {
  const { can, isLoading: entitlementsLoading } = useEntitlements();
  const entitled = can('google_ads_conversions');
  const { stats, isLoadingStats, runSetup, isRunningSetup } = useGoogleAdsConversions(entitled);
  const { formatShortDate } = useCompanyLocale();
  const setup = integration.metadata.conversion_setup;
  const autoSetupTried = useRef(false);

  // First time a connected account has no setup yet (single-account OAuth path
  // skips finalize-connect): run it once, quietly.
  useEffect(() => {
    if (autoSetupTried.current) return;
    if (!entitled || entitlementsLoading) return;
    if (integration.status !== 'connected' || !integration.metadata.customer_id || setup) return;
    autoSetupTried.current = true;
    runSetup().catch((e) => {
      toast({
        title: 'Google Ads conversion setup did not finish',
        description: e instanceof Error ? e.message.slice(0, 300) : 'Unknown error',
        variant: 'destructive',
      });
    });
  }, [entitled, entitlementsLoading, integration.status, integration.metadata.customer_id, setup, runSetup]);

  const handleRepair = async () => {
    try {
      await runSetup();
      toast({ title: 'Google Ads conversion actions are ready' });
    } catch (e) {
      toast({
        title: 'Setup failed',
        description: e instanceof Error ? e.message.slice(0, 300) : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  if (!entitled) {
    return (
      <div className="rounded-lg border p-3 space-y-2 opacity-70">
        <div className="flex items-center gap-2 text-sm font-medium">
          <Lock className="h-4 w-4" /> Conversions to Google Ads
        </div>
        <p className="text-xs text-muted-foreground">
          Sending leads and paid jobs back to Google Ads is part of the Growth plan and above.
          Upgrade to switch it on.
        </p>
      </div>
    );
  }

  if (!integration.metadata.customer_id) return null;

  const formatWhen = (iso: string | null) => {
    if (!iso) return 'never';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return `${formatShortDate(iso)} ${d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;
  };

  return (
    <div className="rounded-lg border p-3 space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-medium">Conversions to Google Ads</div>
        <Button size="sm" variant="outline" onClick={handleRepair} disabled={isRunningSetup}>
          {isRunningSetup
            ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Checking…</>
            : <><RefreshCw className="h-4 w-4 mr-1" /> {setup ? 'Repair setup' : 'Set up'}</>}
        </Button>
      </div>

      <p className="text-xs text-muted-foreground">
        Every new lead is sent as a lead conversion and every completed, paid job as a sale with its value.
        Calls with a Google click ID match the click; the rest match by phone number.
      </p>

      {!setup ? (
        <div className="flex gap-2 rounded-md border border-warning/40 bg-warning/5 p-2 text-xs">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
          <div>Conversion actions have not been created in this Google Ads account yet.</div>
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          <Badge variant="success">Lead and sale actions created</Badge>
          {setup.auto_tagging_enabled
            ? <Badge variant="success">Auto-tagging on</Badge>
            : <Badge variant="destructive-soft">Auto-tagging off</Badge>}
          {setup.ecl_enabled
            ? <Badge variant="success">Phone matching on</Badge>
            : <Badge variant="warning">Phone matching off</Badge>}
        </div>
      )}

      {setup && !setup.auto_tagging_enabled && (
        <div className="flex gap-2 rounded-md border border-destructive/40 bg-destructive/5 p-2 text-xs">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
          <div>
            Auto-tagging is off, so calls cannot be matched to clicks. In Google Ads open
            Admin → Account settings → Auto-tagging and turn it on, then press Repair setup.
          </div>
        </div>
      )}

      {setup && !setup.ecl_enabled && (
        <div className="flex gap-2 rounded-md border border-warning/40 bg-warning/5 p-2 text-xs">
          <AlertTriangle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
          <div>
            Leads without a click ID are matched by phone number, which needs enhanced conversions
            for leads. In Google Ads open Goals → Settings → Enhanced conversions, turn on
            &quot;Enhanced conversions for leads&quot; and accept the terms, then press Repair setup.
          </div>
        </div>
      )}

      <div className="grid grid-cols-3 gap-2 text-xs">
        <div className="rounded-md bg-muted/50 p-2">
          <div className="text-muted-foreground">Sent, last 7 days</div>
          <div className="text-base font-semibold">{isLoadingStats ? '…' : stats?.uploaded ?? 0}</div>
        </div>
        <div className="rounded-md bg-muted/50 p-2">
          <div className="text-muted-foreground">Failed, last 7 days</div>
          <div className={`text-base font-semibold ${stats?.failed ? 'text-destructive' : ''}`}>
            {isLoadingStats ? '…' : stats?.failed ?? 0}
          </div>
        </div>
        <div className="rounded-md bg-muted/50 p-2">
          <div className="text-muted-foreground">Waiting</div>
          <div className="text-base font-semibold">{isLoadingStats ? '…' : stats?.queued ?? 0}</div>
        </div>
      </div>

      <div className="text-xs text-muted-foreground">
        {setup?.account_timezone && (
          <>Conversion times are sent in this account&apos;s time zone ({setup.account_timezone}
          {setup.account_currency ? `, ${setup.account_currency}` : ''}). </>
        )}
        Last sent {formatWhen(integration.metadata.last_upload_at ?? stats?.last_uploaded_at ?? null)}.
        {stats?.reversal_pending ? ` ${stats.reversal_pending} sale(s) were un-paid after sending and need a manual adjustment in Google Ads.` : ''}
      </div>

      {(integration.metadata.last_upload_error || stats?.last_error) && (
        <div className="text-xs text-destructive break-words">
          Last error: {integration.metadata.last_upload_error ?? stats?.last_error}
        </div>
      )}
    </div>
  );
}
